import { LegalPage } from './LegalPage';

interface TermsRouteProps {
  onBackHome: () => void;
}

export function TermsRoute({ onBackHome }: TermsRouteProps) {
  return (
    <LegalPage title="Terms of Service" updated="03/19/2025" onBackHome={onBackHome}>
      <p className="text-foreground font-semibold">
        PLEASE READ THESE TERMS OF SERVICE CAREFULLY. THEY CONTAIN IMPORTANT INFORMATION REGARDING
        YOUR LEGAL RIGHTS, REMEDIES, AND OBLIGATIONS. THESE INCLUDE LIMITATIONS ON LIABILITY AND A
        DISCLAIMER OF ALL WARRANTIES.
      </p>

      <div>
        <h2 className="text-foreground font-semibold">Overview</h2>
        <p className="mt-2">
          These Terms of Service (the &quot;Terms&quot;) constitute a legal agreement between you
          and Sardo Giacomo, VAT number IT02777620069, operating under the name Michelangelo.land
          (&quot; Michelangelo.land,&quot; &quot;Michelangelo,&quot; &quot;we,&quot; &quot;us,&quot;
          or &quot; our&quot;). Your use of our services and platforms is subject to these Terms. By
          using our services, you acknowledge that you have read, understood, and agree to be bound
          by these Terms and to comply with all applicable laws and regulations.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">License to Use Our Services</h2>
        <p className="mt-2">
          Subject to these Terms, we grant you a limited, personal, non-exclusive, non-transferable
          license to use our Services. This license is for personal use only and does not allow
          resale, the transfer not even free of charge or further distribution without our explicit
          consent.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Intellectual Property Rights</h2>
        <p className="mt-2">
          Michelangelo.land owns all rights, titles, and interests in and to our services, including
          all intellectual property rights. This includes our software, interfaces, graphics,
          designs, computer code, products, and all other elements of our services
          (&quot;Michelangelo.land Content&quot;), excluding your content and applications.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">User Content and Data</h2>
        <p className="mt-2">
          You retain all rights to any content, applications, or data you create or process using
          our Services. You are solely responsible for your content and ensuring you have all
          necessary rights to share such content through our Services.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Paid Services</h2>
        <p className="mt-2">
          Some of our Services require payment. By choosing paid Services, you agree to our pricing
          and payment terms. We may modify our fees at any time with notice. Paid accounts may be
          suspended for non-payment without notice.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Service Modifications and Access</h2>
        <p className="mt-2">
          We reserve the right to modify, suspend, or discontinue any part of our Services at any
          time. We may also restrict, suspend, or terminate your access to our Services at our
          discretion, with or without notice.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Privacy and Security</h2>
        <p className="mt-2">
          Your use of our Services is also governed by our Privacy Policy. You are responsible for
          maintaining the security of your account and must notify us immediately of any
          unauthorized access. Consent is given to the use of data also through the use of
          artificial intelligence.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Disclaimer of Warranties</h2>
        <p className="mt-2 text-foreground">
          OUR SERVICES ARE PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT ANY
          WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, WHETHER EXPRESS OR IMPLIED, INCLUDING
          MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE OR FROM DISSERVICES. WE WILL NOT BE
          HELD LIABLE FOR YOUR USE OF OUR SERVICES.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Limitation of Liability</h2>
        <p className="mt-2 text-foreground">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, MICHELANGELO.LAND SHALL NOT BE LIABLE FOR ANY
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF
          OUR SERVICES. IN ANY CASE, THE DAMAGE THAT CAN BE COMPENSATED WILL NOT EXCEED THE VALUE OF
          THE COMPENSATION PAID TO US FOR THE SERVICE.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Prohibition of Unlawful Use Clause</h2>
        <p className="mt-2">
          The User undertakes to use the Application only for lawful purposes and in accordance with
          applicable regulations. You are expressly prohibited from using the Application for
          unlawful, fraudulent, misleading purposes or that may cause harm to third parties,
          including but not limited to:
        </p>
        <ul className="mt-3 list-disc pl-5 space-y-1">
          <li>
            The violation of laws, regulations or rights of third parties, including intellectual
            property rights, privacy or protection from;
          </li>
          <li>
            Access, distribution or transmission of illegal, offensive, defamatory, discriminatory,
            threatening or inciting violence or hatred content;
          </li>
          <li>
            Use of the application for hacking, phishing, spreading malware, spyware or any other
            form of attack;
          </li>
          <li>
            Use of the Application for unauthorized, commercial purposes or to promote services,
            goods or activities not permitted by the Terms of Service.
          </li>
        </ul>
        <p className="mt-3">
          Failure to comply with this clause may result in the suspension or termination of access
          to the Application, as well as any reports to the competent authorities in the cases
          provided for by law.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Modifications and Tolerances</h2>
        <p className="mt-2">
          Any modification, addition or derogation to these contractual conditions must be made only
          in writing and specifically approved by Michelangelo. Any behaviour of tolerance or
          practices adopted during the execution of the contract shall in no way constitute a tacit
          modification of the present conditions nor generate binding rights or obligations for
          Michelangelo.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Governing Law</h2>
        <p className="mt-2">
          These Terms shall be governed by and construed in accordance with the laws of Italy,
          without regard to its conflict of law provisions.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Arbitration Clause</h2>
        <p className="mt-2">
          Any dispute shall be settled by a sole arbitrator appointed, at the request of the most
          diligent party, by the President of the Court of Genoa (Italy). The arbitrator shall
          decide without any formalities and in accordance with principles of equity. The
          arbitrator&apos;s fee shall be borne equally by the parties.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Changes to Terms</h2>
        <p className="mt-2">
          We may update these Terms at any time. We will notify users of material changes via email
          or through our Services. Your continued use of our Services after such changes constitutes
          acceptance of the updated Terms.
        </p>
      </div>

      <div>
        <h2 className="text-foreground font-semibold">Contact Information</h2>
        <p className="mt-2">
          For questions about these Terms or complaints or claims, please contact us at:
        </p>
        <div className="mt-4 rounded-xl border border-border/70 bg-card/40 p-4">
          <div className="text-foreground font-medium">Michelangelo.land</div>
          <div className="mt-2 text-muted-foreground">
            Attention: Sardo Giacomo
            <br />
            Email:{' '}
            <a
              className="text-foreground underline underline-offset-4"
              href="mailto:sardo@michelangelo.land"
            >
              sardo@michelangelo.land
            </a>
          </div>
        </div>
      </div>
    </LegalPage>
  );
}
